import type { ReactNode } from 'react';

import {
  Tile,
  TileImage,
  TileContent,
  TileTitle,
  TileMeta,
  type TileItemType
} from '../Tile';

import styles from './tilePreview.module.scss';

export type TilePreviewProps = {
  /**
   * Item type for the tile (affects styling)
   */
  itemType?: TileItemType;
  /**
   * Image URL to display
   */
  imageUrl?: string | null;
  /**
   * Alt text for the image
   */
  imageAlt?: string;
  /**
   * Main title text
   */
  title: string;
  /**
   * Optional meta/subtitle text
   */
  meta?: string;
  /**
   * Aria label for accessibility
   */
  ariaLabel?: string;
  /**
   * Optional click handler (makes tile interactive)
   */
  onClick?: () => void;
  /**
   * Additional class name
   */
  className?: string;
  /**
   * Optional custom content to render instead of default title/meta
   */
  children?: ReactNode;
};

/**
 * TilePreview - A horizontal tile for displaying selected entity previews.
 *
 * Used in entity selectors to show the currently selected item.
 * Renders a compact, horizontal tile with image, title, and optional meta.
 *
 * @example
 * ```tsx
 * <TilePreview
 *   itemType="breedingDog"
 *   imageUrl={dog.imageUrl}
 *   title={dog.name || 'Unnamed'}
 *   meta="Female"
 * />
 * ```
 */
export function TilePreview({
  itemType,
  imageUrl,
  imageAlt,
  title,
  meta,
  ariaLabel,
  onClick,
  className,
  children
}: TilePreviewProps) {
  const combinedClassName = [styles.tilePreview, className]
    .filter(Boolean)
    .join(' ');

  return (
    <Tile
      itemType={itemType}
      horizontal
      className={combinedClassName}
      onClick={onClick}
      ariaLabel={ariaLabel || `Selected: ${title}`}
    >
      <TileImage
        imageUrl={imageUrl}
        alt={imageAlt || title}
        className={styles.tilePreviewImage}
      />
      <TileContent>
        {children ?? (
          <>
            <TileTitle>{title}</TileTitle>
            {meta && <TileMeta>{meta}</TileMeta>}
          </>
        )}
      </TileContent>
    </Tile>
  );
}
